/**
 * HomePage — Browse-first FoodPhotos 2.0 homepage. Matches reference UI
 * pixel-closely on mobile; preserves existing desktop header per spec.
 *
 * Layout order (mobile & desktop reading flow):
 *   Header → Prompt line → Headline → Credibility → Info blocks (₹pricing, library)
 *   → Search bar (primary CTA) → "Try searching" divider → Chips w/ icons
 *   → 4-column icon trust strip → Image library grid
 *
 * Preserves: search logic, auth, OTP flow, image grid, premium/download/plans,
 * analytics, dashboard, WhatsApp automation.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { imagesAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Loader2, Search, X, ChevronDown, ArrowRight,
  Camera, ShieldCheck, Zap, Users2, Image as ImageIcon,
  Pizza, Sandwich, Flame, Wheat, Croissant, Cake, Milk,
} from "lucide-react";
import { toast } from "sonner";
import { trackInternalEvent } from "@/lib/analytics";
import { ImageCard } from "@/components/dashboard/ImageCard";
import { HeroCollage } from "@/components/HeroCollage";
import { HeroCollageMobile } from "@/components/HeroCollageMobile";
import PreviewModal from "@/components/PreviewModal";
import OtpGateModal from "@/components/OtpGateModal";

const DESKTOP_LOGO = "https://customer-assets.emergentagent.com/job_57dcb951-2bbd-452d-9917-248d638c6a0f/artifacts/fz5mk4hp_Logo%20food%20photos%20%289%29.webp";

// Reference chip set with icons
const CHIPS = [
  { key: "Pizza", Icon: Pizza },
  { key: "Burger", Icon: Sandwich },
  { key: "Paneer Tikka", Icon: Flame },
  { key: "Biryani", Icon: Wheat },
  { key: "Momos", Icon: Croissant },
  { key: "Cake", Icon: Cake },
  { key: "Shake", Icon: Milk },
];

// Curly hand-drawn arrow SVG (points down-right) — matches reference
function CurlyArrow({ className = "" }) {
  return (
    <svg viewBox="0 0 40 40" fill="none" className={className} aria-hidden="true">
      <path
        d="M6 8 C 14 6, 22 8, 26 16 C 28 20, 26 26, 22 28"
        stroke="#F97316" strokeWidth="2" strokeLinecap="round" fill="none"
      />
      <path d="M18 24 L22 28 L26 24" stroke="#F97316" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
}

// Mobile-only wordmark (matches reference — big, on-brand, includes tagline)
function MobileLogo() {
  return (
    <div className="flex flex-col leading-none select-none" data-testid="homepage-logo-mobile">
      <div className="flex items-baseline">
        <span
          className="text-[1.7rem] font-black tracking-tight text-[#0F172A]"
          style={{ fontFamily: "Inter, sans-serif", letterSpacing: "-0.035em" }}
        >
          FOOD
        </span>
        <span
          className="text-[1.7rem] font-black tracking-tight text-[#F97316]"
          style={{ fontFamily: "Inter, sans-serif", letterSpacing: "-0.035em" }}
        >
          PHOTOS
        </span>
        <span className="ml-1 -translate-y-1.5 inline-block">
          <svg width="12" height="12" viewBox="0 0 10 10" fill="none" aria-hidden="true">
            <path d="M0 0 H8 V2 H2 V8 H0 Z" fill="#F97316" />
          </svg>
        </span>
      </div>
      <span
        className="text-[0.82rem] text-[#334155] mt-1 font-medium"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        by <span className="font-bold text-[#0F172A]">Studio Pass 2.0</span>
      </span>
    </div>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [images, setImages] = useState([]);
  const [fillerImages, setFillerImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [vegOnly, setVegOnly] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [otpOpen, setOtpOpen] = useState(false);
  const [otpMode, setOtpMode] = useState("free_sample");
  const [otpReturnTo, setOtpReturnTo] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [showStickySearch, setShowStickySearch] = useState(false);

  // Whitelist of paths the sign-in modal is allowed to redirect back to
  // after successful verification. Blocks open-redirect via ?returnTo=...
  const SAFE_RETURN_PATHS = ["/dashboard", "/admin"];

  // Auto-open the existing-user Sign In modal when arriving with ?signin=1
  // (e.g. after being bounced from a protected route, or via legacy /login).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("signin") === "1") {
      const rt = params.get("returnTo");
      setOtpReturnTo(rt && SAFE_RETURN_PATHS.includes(rt) ? rt : null);
      setOtpMode("existing_user_signin");
      setOtpOpen(true);
      // Clean the query params but keep the user on `/`.
      const url = new URL(window.location.href);
      url.searchParams.delete("signin");
      url.searchParams.delete("returnTo");
      window.history.replaceState({}, "", url.pathname + (url.search ? url.search : ""));
    }
  }, []);

  useEffect(() => {
    if (!authLoading && user) navigate("/dashboard", { replace: true });
  }, [authLoading, user, navigate]);

  const openSigninModal = () => {
    setPendingAction(null);
    setOtpMode("existing_user_signin");
    setOtpReturnTo(null);
    setOtpOpen(true);
  };

  const seenIdsRef = useRef(new Set());
  const debounceRef = useRef(null);
  const searchInputRef = useRef(null);
  const heroRef = useRef(null);

  useEffect(() => {
    trackInternalEvent("HomepageView", { metadata: { page: "home" } });
  }, []);

  const fetchImages = useCallback(async (currentSearch, vegFilter, cursor = null, append = false) => {
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const params = { limit: 30 };
      if (currentSearch.trim()) {
        params.search = currentSearch.trim();
        if (cursor !== null && cursor < 0) params.page = Math.abs(cursor);
      } else if (cursor !== null && cursor >= 0) {
        params.cursor = cursor;
      }
      if (vegFilter) params.veg_only = true;

      const res = await imagesAPI.list(params);
      const newImages = res.data.images || [];
      if (append) {
        const unique = newImages.filter((img) => {
          if (seenIdsRef.current.has(img.public_id)) return false;
          seenIdsRef.current.add(img.public_id);
          return true;
        });
        setImages((prev) => [...prev, ...unique]);
      } else {
        seenIdsRef.current.clear();
        newImages.forEach((img) => seenIdsRef.current.add(img.public_id));
        setImages(newImages);
      }
      setNextCursor(res.data.next_cursor);
      setFillerImages(res.data.filler_images || []);
    } catch {
      toast.error("Failed to load images");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchImages(search, vegOnly, null, false);
  }, [search, vegOnly, fetchImages]);

  const handleSearchChange = (value) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(value);
      if (value.trim()) trackInternalEvent("HomepageSearch", { metadata: { keyword: value.trim() } });
    }, 350);
  };

  const scrollToLibrary = useCallback(() => {
    setTimeout(() => {
      document.getElementById("home-library")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  }, []);

  const handleSubmitSearch = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSearch(searchInput);
    if (searchInput.trim()) trackInternalEvent("HomepageSearch", { metadata: { keyword: searchInput.trim(), source: "button" } });
    scrollToLibrary();
  };

  const handleChipClick = (term) => {
    setSearchInput(term);
    setSearch(term);
    trackInternalEvent("HomepageSearch", { metadata: { keyword: term, source: "chip" } });
    scrollToLibrary();
  };

  const clearSearch = () => {
    setSearchInput("");
    setSearch("");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    searchInputRef.current?.focus();
  };

  const handleLoadMore = () => {
    if (nextCursor !== null && !loadingMore) fetchImages(search, vegOnly, nextCursor, true);
  };

  useEffect(() => {
    const onScroll = () => {
      if (loadingMore || loading || nextCursor === null) return;
      const nearBottom = window.innerHeight + window.scrollY >= document.body.offsetHeight - 800;
      if (nearBottom) handleLoadMore();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingMore, loading, nextCursor]);

  useEffect(() => {
    const onScroll = () => {
      const el = heroRef.current;
      if (!el) return;
      const bottom = el.getBoundingClientRect().bottom;
      setShowStickySearch(bottom < 60);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleImageClick = (img) => {
    trackInternalEvent("AnonPreviewOpened", {
      product_id: img.public_id,
      metadata: { dish_name: img.dish_name, is_premium: !!img.is_premium, search },
    });
    setPreviewImage(img);
  };

  const handleAnonDownloadIntent = (img) => {
    trackInternalEvent("DownloadClickedAnon", {
      product_id: img?.public_id,
      metadata: { dish_name: img?.dish_name || "", search },
    });
    setPendingAction({
      source: "homepage_download",
      previewImage: img
        ? { public_id: img.public_id, dish_name: img.dish_name, secure_url: img.secure_url, thumbnail_url: img.thumbnail_url, is_premium: !!img.is_premium, veg_nonveg: img.veg_nonveg }
        : null,
      previewImageId: img?.public_id || null,
      search: search || "",
    });
    setOtpMode("free_sample");
    setOtpReturnTo(null);
    setOtpOpen(true);
  };

  const suggestions = search.trim()
    ? Array.from(new Set(search.trim().split(/\s+/).filter((w) => w.length >= 2))).slice(0, 4)
    : [];
  const showNoResults = !loading && images.length === 0 && search.trim().length > 0;

  if (authLoading || user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2]">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7F2]" data-testid="homepage">
      {/* ── HEADER ────────────────────────────────────────────────── */}
      <header className="hidden lg:block sticky top-0 z-40 bg-[#FAF7F2]/95 backdrop-blur-sm border-b border-slate-200/50" data-testid="homepage-header">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 h-[72px] sm:h-16 flex items-center justify-between gap-3">
          {/* Mobile logo (larger, prominent) */}
          <div className="lg:hidden">
            <MobileLogo />
          </div>
          {/* Desktop logo (unchanged asset) */}
          <img
            src={DESKTOP_LOGO}
            alt="FoodPhotos 2.0"
            className="hidden lg:block h-11 w-[210px] object-cover object-center"
            data-testid="homepage-logo"
          />

          {/* Sticky compact search bar - visible only after hero scrolls out (desktop-only to avoid crowding mobile header) */}
          <div
            className={`hidden lg:flex flex-1 max-w-[420px] transition-all duration-300 ${
              showStickySearch ? "opacity-100 translate-y-0" : "opacity-0 pointer-events-none -translate-y-1"
            }`}
            data-testid="homepage-sticky-search-wrap"
          >
            <div className="homepage-search-mini flex items-center gap-2 bg-white border border-slate-300 rounded-full pl-4 pr-1.5 py-1.5 w-full">
              <Search className="h-4 w-4 text-slate-400 shrink-0" />
              <input
                data-testid="homepage-sticky-search-input"
                type="text"
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSubmitSearch()}
                placeholder="Search your dish..."
                className="flex-1 bg-transparent outline-none text-sm text-slate-900 placeholder:text-slate-400 min-w-0"
              />
              <button
                data-testid="homepage-sticky-search-btn"
                onClick={handleSubmitSearch}
                aria-label="Search"
                className="shrink-0 h-8 w-8 rounded-full text-white flex items-center justify-center transition-transform hover:scale-105 active:scale-95"
                style={{ background: "linear-gradient(135deg,#F59E0B,#F97316)" }}
              >
                <Search className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex flex-col items-end leading-tight shrink-0">
            <span className="text-[0.7rem] sm:text-xs text-slate-500 font-medium" data-testid="homepage-signin-prefix">
              Already Registered?
            </span>
            <button
              data-testid="homepage-signin-btn-desktop"
              onClick={openSigninModal}
              className="inline-flex items-center gap-1 text-sm sm:text-base font-bold text-orange-600 hover:text-orange-700 transition-colors"
            >
              Sign In <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </header>

      {/* ── HERO ──────────────────────────────────────────────────── */}
      <section ref={heroRef} className="relative max-w-7xl mx-auto px-5 sm:px-6 pt-6 sm:pt-10 pb-4 sm:pb-6 overflow-x-clip" data-testid="homepage-hero">
        {/* ─────────────── MOBILE HERO (row-anchored composition) ─────────────
            Each food image is absolutely-positioned INSIDE its own text row so
            the image is visually attached to that specific copy section. */}
        <div className="-mx-5 -mt-6" data-testid="mobile-hero">
          <div className="mx-auto px-5 pt-6 pb-4" style={{ maxWidth: "600px", background: "#FAF7F2" }}>
            {/* HEADER — STUDIO PASS 2.0 wordmark + Already Registered / Sign In */}
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="leading-none" style={{ fontFamily: "Impact, 'Arial Black', sans-serif", fontSize: "29px", letterSpacing: "0.3px" }}>
                  <span className="text-[#06070b]">STUDIO PASS </span>
                  <span className="text-[#ff3d00]">2.0</span>
                </div>
                <div className="mt-1 text-[12.5px] font-semibold text-[#06070b] whitespace-nowrap">
                  Pre Approved <span className="text-[#ed1638]">Zomato</span>{" "}
                  <span className="text-[#ff5a00]">Swiggy</span> Photos
                </div>
              </div>
              <div className="text-right text-[12px] leading-tight text-[#06070b]">
                Already Registered?
                <button
                  onClick={openSigninModal}
                  className="mt-2.5 inline-flex items-center gap-1.5 text-[16px] text-[#ff3d00]"
                  data-testid="homepage-signin-btn"
                >
                  Sign In <span className="text-[21px] leading-none">→</span>
                </button>
              </div>
            </div>

            {/* PROMPT */}
            <p
              className="mt-9 text-center text-[16px] font-semibold leading-[1.5] text-[#06070b]"
              data-testid="homepage-explore-prompt"
            >
              Search any dish to explore<br />
              the library{" "}
              <span style={{ textDecoration: "underline", textDecorationColor: "#ff3d00", textUnderlineOffset: "5px", textDecorationThickness: "1px" }}>
                instantly.
              </span>
            </p>

            {/* HEADLINE */}
            <h1
              className="mx-auto max-w-[400px] text-center font-black text-[#06070b]"
              style={{ fontFamily: "Impact, 'Arial Black', sans-serif", fontSize: "42px", fontWeight: 900, lineHeight: 1.04, letterSpacing: "0.1px" }}
              data-testid="homepage-headline"
            >
              Get More
              <span className="block text-[#ff3d00]">Online Orders</span>
              With Professional<br />
              Food Photos
            </h1>

            {/* CREDIBILITY */}
            <p className="mt-2 text-center italic text-[17px] text-[#65687b]" data-testid="homepage-credibility">
              (Real. Not AI Generated.)
            </p>

            {/* 5-TILE COLLAGE */}
            <section
              className="relative mt-4 rounded-[23px] overflow-hidden bg-white"
              style={{ height: "430px", border: "4px solid #fff", boxShadow: "0 11px 22px rgba(59, 26, 11, 0.14)" }}
              data-testid="hero-collage"
              aria-label="Studio Pass food photo examples"
            >
              {/* Thali — large top-left */}
              <div className="absolute overflow-hidden bg-[#ddd]" style={{ left: 0, top: 0, width: "61%", height: "65.5%", borderBottomRightRadius: "27px", clipPath: "polygon(0 0,100% 0,94% 100%,0 100%)" }} data-testid="collage-card-thali">
                <img src="https://customer-assets.emergentagent.com/job_57dcb951-2bbd-452d-9917-248d638c6a0f/artifacts/fysm1ttd_Fam%27s%20Favorite%20Matar%20Paneer%20-%20Feast%20Thali.jpg" alt="Matar paneer feast thali" referrerPolicy="no-referrer" loading="eager" className="tile-fade block w-full h-full object-cover" style={{ objectPosition: "51% 48%", animationDelay: "0ms" }} />
              </div>
              {/* Burger — top-right */}
              <div className="absolute overflow-hidden bg-[#ddd]" style={{ right: 0, top: 0, width: "40%", height: "36%", borderBottomLeftRadius: "25px", clipPath: "polygon(7% 0,100% 0,100% 100%,0 100%)" }} data-testid="collage-card-burger">
                <img src="https://customer-assets.emergentagent.com/job_57dcb951-2bbd-452d-9917-248d638c6a0f/artifacts/p3hzfzsu_Fried%20Chicken%20Burger.png" alt="Fried chicken burger" referrerPolicy="no-referrer" loading="eager" className="tile-fade block w-full h-full object-cover" style={{ objectPosition: "center 50%", animationDelay: "100ms" }} />
              </div>
              {/* Pizza — mid-right */}
              <div className="absolute overflow-hidden bg-[#ddd]" style={{ right: 0, top: "36.4%", width: "42.4%", height: "29.1%", borderTopLeftRadius: "18px", clipPath: "polygon(3% 0,100% 0,100% 100%,0 100%)" }} data-testid="collage-card-pizza">
                <img src="https://customer-assets.emergentagent.com/job_57dcb951-2bbd-452d-9917-248d638c6a0f/artifacts/98f36ih8_Paneer%20Pizza.png" alt="Paneer pizza" referrerPolicy="no-referrer" loading="eager" className="tile-fade block w-full h-full object-cover" style={{ objectPosition: "52% 51%", animationDelay: "200ms" }} />
              </div>
              {/* Frappe — bottom-left */}
              <div className="absolute overflow-hidden bg-[#ddd]" style={{ left: 0, bottom: 0, width: "51.2%", height: "34%", borderTopRightRadius: "20px", clipPath: "polygon(0 0,100% 0,92% 100%,0 100%)" }} data-testid="collage-card-frappe">
                <img src="https://customer-assets.emergentagent.com/job_57dcb951-2bbd-452d-9917-248d638c6a0f/artifacts/d5hbydk3_Chocolate%20Frappe%20Coffee.png" alt="Chocolate frappe" referrerPolicy="no-referrer" loading="eager" className="tile-fade block w-full h-full object-cover" style={{ objectPosition: "50% 54%", animationDelay: "300ms" }} />
              </div>
              {/* Bowl — bottom-right */}
              <div className="absolute overflow-hidden bg-[#ddd]" style={{ right: 0, bottom: 0, width: "49.8%", height: "34%", borderTopLeftRadius: "20px", clipPath: "polygon(8% 0,100% 0,100% 100%,0 100%)" }} data-testid="collage-card-bowl">
                <img src="https://customer-assets.emergentagent.com/job_57dcb951-2bbd-452d-9917-248d638c6a0f/artifacts/mnbyrj9a_Manchurian%20Paneer%20Bowl.jpg" alt="Manchurian paneer bowl" referrerPolicy="no-referrer" loading="eager" className="tile-fade block w-full h-full object-cover" style={{ objectPosition: "50% 47%", animationDelay: "400ms" }} />
              </div>
            </section>

            {/* PRICING */}
            <p className="mt-4 text-center text-[20px] font-semibold leading-[1.38] text-[#06070b]" data-testid="homepage-info-pricing">
              Professional food photoshoots<br />
              often cost <span className="text-[#ff3d00] text-[26px] font-extrabold">₹20,000</span> or more.
            </p>

            {/* OFFER CARD */}
            <section
              className="mx-auto mt-2 mb-6 text-center leading-[1.35]"
              style={{ width: "74%", padding: "10px 8px", border: "1px solid #ff7049", borderRadius: "14px", fontSize: "17px" }}
              data-testid="homepage-info-library"
              aria-label="Studio Pass pricing"
            >
              <span className="text-[#ff3d00] font-bold">Studio Pass 2.0</span> starts with
              <strong className="block text-[#ff3d00] text-[23px]">Free Sample Downloads</strong>
              <span className="block mt-1">thereafter</span>
              <span className="text-[#ff3d00] text-[23px] font-extrabold">₹7.5</span>{" "}
              <b className="text-[21px]">Per photo</b>
            </section>

            {/* 4-COLUMN PROOF STRIP */}
            <section className="grid grid-cols-4 -mx-[3px] mb-7" aria-label="Studio Pass benefits">
              {[
                {
                  icon: <ImageIcon className="w-5 h-5" />,
                  lines: [<>15,000+ Real</>, <>Food Photos</>],
                  sub: <>(<span className="text-[#ff3d00]">Not AI Generated</span>)</>,
                },
                {
                  icon: <Users2 className="w-5 h-5" />,
                  lines: [<>Trusted by</>, <><span className="text-[#ff3d00]">1650+</span> restaurants</>, <>across India</>],
                  sub: null,
                },
                {
                  icon: <ShieldCheck className="w-5 h-5" />,
                  lines: [<>Pre Approved by</>, <><span className="text-[#ed1638]">Zomato</span> & <span className="text-[#ff5a00]">Swiggy</span></>],
                  sub: null,
                },
                {
                  icon: <Zap className="w-5 h-5" />,
                  lines: [<>Download <span className="text-[#ff3d00]">Free</span></>, <><span className="text-[#ff3d00]">Samples</span> and Verify</>],
                  sub: null,
                },
              ].map((item, i) => (
                <div key={i} className={`min-w-0 px-1.5 text-center leading-[1.38] ${i > 0 ? "border-l border-[#c9c4c1]" : ""}`} style={{ fontSize: "10.5px" }}>
                  <div className="mx-auto mb-2 w-[46px] h-[46px] rounded-full border border-[#ffe6db] grid place-items-center text-[#ff3d00]" style={{ boxShadow: "0 5px 13px rgba(212,179,165,0.14)" }}>
                    {item.icon}
                  </div>
                  {item.lines.map((line, li) => (
                    <div key={li} className="font-bold text-[11px]">{line}</div>
                  ))}
                  {item.sub && <div className="mt-0.5 font-normal">{item.sub}</div>}
                </div>
              ))}
            </section>

            {/* SEARCH BAR */}
            <form
              onSubmit={(e) => { e.preventDefault(); handleSubmitSearch(); }}
              className="flex items-center gap-4 bg-white"
              style={{ height: "72px", padding: "8px 8px 8px 22px", border: "1.7px solid #ff7950", borderRadius: "50px", boxShadow: "0 9px 22px rgba(221, 105, 50, 0.15)" }}
              data-testid="homepage-search-bar-mobile"
              role="search"
            >
              <Search className="w-6 h-6 text-[#8384c0] shrink-0" strokeWidth={3} />
              <input
                type="search"
                aria-label="Search your dish"
                placeholder="Search your dish..."
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                data-testid="homepage-search-input-mobile"
                className="flex-1 min-w-0 border-0 outline-none bg-transparent text-[17px] text-[#17171c] placeholder:text-[#8788bd]"
              />
              <button
                type="submit"
                aria-label="Search"
                data-testid="homepage-search-btn-mobile"
                className="shrink-0 grid place-items-center"
                style={{ width: "76px", height: "56px", borderRadius: "40px", background: "linear-gradient(135deg,#ff2900,#ff5200)" }}
              >
                <Search className="w-6 h-6 text-white" strokeWidth={3} />
              </button>
            </form>

            {/* TRY SEARCHING DIVIDER */}
            <div className="flex items-center justify-center gap-4 mt-4 mb-2.5 text-[16px] font-bold">
              <span className="h-px w-[60px]" style={{ background: "linear-gradient(90deg,transparent,#ddd)" }} />
              <span>Try searching</span>
              <span className="h-px w-[60px]" style={{ background: "linear-gradient(90deg,#ddd,transparent)" }} />
            </div>

            {/* CHIPS — 2-column grid on mobile */}
            <div className="grid grid-cols-2 gap-x-3 gap-y-2" data-testid="homepage-chips">
              {[
                { emoji: "🍛", label: "Thali" },
                { emoji: "🍱", label: "Combo" },
                { emoji: "🍕", label: "Pizza" },
                { emoji: "🍢", label: "Chaap" },
                { emoji: "🥟", label: "Momos" },
                { emoji: "🍰", label: "Cake" },
                { emoji: "🥤", label: "Shakes" },
                { emoji: "🍔", label: "Burger" },
              ].map(({ emoji, label }) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => handleChipClick(label)}
                  data-testid={`homepage-chip-${label.toLowerCase()}`}
                  className="flex items-center justify-center whitespace-nowrap"
                  style={{ height: "37px", border: "1px solid #f0ddd4", borderRadius: "22px", background: "#fffaf7", color: "#18191e", fontSize: "16px", boxShadow: "0 4px 11px rgba(167,111,80,0.06)" }}
                >
                  <span className="mr-2 text-[18px]">{emoji}</span>
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ─────────────── DESKTOP HERO — now unused (mobile block renders on all viewports) ─────────────── */}
        <div className="hidden lg:grid-cols-[1.2fr_1fr] lg:gap-14 lg:items-center" style={{ display: "none" }}>
          <div className="relative z-10 min-w-0">
            <div className="flex items-start gap-2.5 mb-8" data-testid="homepage-explore-prompt-desktop">
              <CurlyArrow className="w-9 h-9 shrink-0 -mt-1" />
              <p className="homepage-prompt-line pt-1">
                Search any dish to explore the library{" "}
                <span className="homepage-prompt-underline">instantly.</span>
              </p>
            </div>
            <h1 className="homepage-headline text-slate-900" data-testid="homepage-headline-desktop">
              Get More <br />
              <span className="accent">Online Orders</span> <br />
              With Professional <br />
              Food Photos
            </h1>
            <p className="homepage-credibility mt-5">(Real. Not AI Generated.)</p>
            <div className="flex items-start gap-3.5 mt-10">
              <span className="info-icon-outlined"><Camera className="w-4 h-4" /></span>
              <p className="homepage-info-copy pt-1">
                Professional food photoshoots often cost <span className="num">₹20,000</span> or more.{" "}
                <span className="bold">Studio Pass starts at just <span className="num">₹249</span>.</span>
              </p>
            </div>
            <div className="h-px bg-slate-200 my-6" />
            <div className="flex items-start gap-3.5">
              <span className="info-icon-outlined"><ShieldCheck className="w-4 h-4" /></span>
              <p className="homepage-info-copy pt-1">
                Search from <span className="num">15,000+</span> real food photos already approved for Zomato & Swiggy menus. Preview instantly and download <span className="num">2 free sample photos</span>.
              </p>
            </div>
          </div>
          <div className="flex justify-center self-center h-[520px]">
            <div className="w-full h-full relative">
              <HeroCollage />
            </div>
          </div>
        </div>

        <div style={{ display: "none" }} data-testid="homepage-search-bar-shared">
          <Search className="h-5 w-5 sm:h-6 sm:w-6 text-slate-400 shrink-0" />
          <input
            ref={searchInputRef}
            data-testid="homepage-search-input"
            type="text"
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSubmitSearch()}
            placeholder="Search your dish..."
            className="flex-1 bg-transparent outline-none text-base sm:text-lg text-slate-900 placeholder:text-slate-400 min-w-0 py-1"
          />
          {searchInput && (
            <button
              data-testid="homepage-search-clear"
              onClick={clearSearch}
              className="p-1.5 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors shrink-0"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          {/* Reference has a PILL-shaped orange button (not a circle) */}
          <button
            data-testid="homepage-search-btn"
            onClick={handleSubmitSearch}
            className="shrink-0 h-11 sm:h-12 px-6 sm:px-8 rounded-full text-white flex items-center justify-center shadow-[0_4px_14px_-2px_rgba(249,115,22,0.5)] hover:shadow-[0_6px_20px_-2px_rgba(249,115,22,0.65)] transition-all active:scale-[0.98]"
            style={{ background: "linear-gradient(135deg,#F59E0B,#F97316)" }}
            aria-label="Search"
          >
            <Search className="h-5 w-5" />
          </button>
        </div>

        {/* ── "Try searching" divider + chips (mobile block has its own) ─────────── */}
        <div style={{ display: "none" }}>
          <div className="section-divider-label mb-4 px-4">
            <span>Try searching</span>
          </div>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-2.5" data-testid="homepage-chips">
            {CHIPS.map(({ key, Icon }) => (
              <button
                key={key}
                data-testid={`homepage-chip-${key.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => handleChipClick(key)}
                className="chip-icon"
              >
                <Icon className="chip-svg w-4 h-4" />
                <span>{key}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ── Trust strip (mobile block has its own) ────────────────────────────── */}
        <div style={{ display: "none" }} data-testid="homepage-trust-strip">
          <div className="trust-item">
            <span className="trust-icon-wrap"><ImageIcon className="w-6 h-6 sm:w-7 sm:h-7" /></span>
            <span className="trust-label">15,000+</span>
            <span className="trust-sub">Real Food Photos</span>
          </div>
          <div className="trust-divider" aria-hidden="true" />
          <div className="trust-item">
            <span className="trust-icon-wrap"><Users2 className="w-6 h-6 sm:w-7 sm:h-7" /></span>
            <span className="trust-label">Trusted by</span>
            <span className="trust-sub">1,650+ Restaurants</span>
            <span className="trust-sub">Across India</span>
          </div>
          <div className="trust-divider" aria-hidden="true" />
          <div className="trust-item">
            <span className="trust-icon-wrap"><ShieldCheck className="w-6 h-6 sm:w-7 sm:h-7" /></span>
            <span className="trust-label">Pre-approved by</span>
            <span className="trust-sub">Zomato & Swiggy</span>
          </div>
          <div className="trust-divider" aria-hidden="true" />
          <div className="trust-item">
            <span className="trust-icon-wrap"><Zap className="w-6 h-6 sm:w-7 sm:h-7" /></span>
            <span className="trust-label">Instant</span>
            <span className="trust-sub">Downloads</span>
          </div>
        </div>
      </section>

      {/* ── IMAGE LIBRARY ─────────────────────────────────────────── */}
      <section id="home-library" className="max-w-7xl mx-auto px-4 sm:px-6 pt-4 sm:pt-8 pb-16" data-testid="homepage-library">
        <div className="flex items-center justify-end mb-3 sm:mb-4">
          <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="homepage-veg-toggle-label">
            <input
              type="checkbox"
              checked={vegOnly}
              onChange={(e) => setVegOnly(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
              data-testid="homepage-veg-toggle"
            />
            <span className="text-sm text-slate-700">Veg only</span>
          </label>
        </div>

        {loading && images.length === 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="aspect-[4/3] rounded-lg bg-slate-100 animate-pulse" />
            ))}
          </div>
        ) : showNoResults ? (
          <div className="py-16 text-center" data-testid="homepage-no-results">
            <p className="text-lg font-semibold text-slate-900">
              We couldn&apos;t find &ldquo;{search.trim()}&rdquo;.
            </p>
            {suggestions.length > 0 && (
              <>
                <p className="mt-2 text-sm text-slate-600">Try searching</p>
                <div className="mt-3 flex flex-wrap justify-center gap-2">
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      data-testid={`homepage-suggestion-${s.toLowerCase()}`}
                      onClick={() => handleChipClick(s)}
                      className="chip-icon"
                    >
                      <Search className="chip-svg w-4 h-4" />
                      <span>{s}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {images.map((img) => (
                <ImageCard key={img.public_id} image={img} onClick={handleImageClick} isFree={true} />
              ))}
              {fillerImages.map((img) => (
                <ImageCard key={`filler-${img.public_id}`} image={img} onClick={handleImageClick} isFree={true} />
              ))}
            </div>

            {loadingMore && (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
              </div>
            )}
            {!loadingMore && nextCursor === null && images.length > 0 && (
              <p className="text-center text-sm text-slate-500 py-8">
                You&apos;ve reached the end. Sign in to unlock the full library.
              </p>
            )}
            {!loadingMore && nextCursor !== null && (
              <div className="flex justify-center py-8">
                <Button variant="outline" onClick={handleLoadMore} data-testid="homepage-load-more-btn" className="border-slate-300">
                  Load more <ChevronDown className="ml-1.5 h-4 w-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </section>

      {previewImage && (
        <PreviewModal
          image={previewImage}
          images={images}
          onClose={() => setPreviewImage(null)}
          onNavigate={(img) => setPreviewImage(img)}
          user={null}
          isAnonymous={true}
          onAnonymousDownload={(img) => handleAnonDownloadIntent(img)}
          onUserUpdate={() => {}}
          onShowPricing={() => {}}
          showPremiumLock={true}
        />
      )}

      <OtpGateModal
        open={otpOpen}
        onClose={() => setOtpOpen(false)}
        pendingAction={pendingAction}
        mode={otpMode}
        returnTo={otpReturnTo}
      />
    </div>
  );
}
